import { useQuery } from "@apollo/client";
import { ArrowUpIcon, PaperClipIcon, XIcon } from "@heroicons/react/outline";
import { Editor } from "@tiptap/core";
import { truncate } from "lodash";
import {
  ChangeEvent,
  ClipboardEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
  SkillsQuery,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import ChatHistoryPopover from "@apps/home/components/chat-history-popover";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import cache, {
  currentOrganizationVar,
  errorNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import Input from "@components/input/input";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import AppPopover from "@components/popover/app-popover";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  TopicflowIconType,
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon-helpers";
import { asyncUploadImage } from "@components/wysiwyg/extensions/image-helpers";
import TextOnlyWysiwyg from "@components/wysiwyg/text-only-wysiwyg";
import { classNames } from "@helpers/css";

import { ChatSessionContext } from "../ask-ai-context";
import getAskAiSessionQuery from "../graphql/get-ask-ai-session-query";
import skillsQuery from "../graphql/skills-query";
import { getChatSessionTitle } from "../helpers";
import SkillCommands from "../wysiwyg/skill-commands-extension";

export type AskAIImageAttachment = {
  id: string;
  filename: string;
  previewUrl: string;
  imageUrl?: string;
  isUploading: boolean;
};

const maxAskAIImageAttachments = 4;
const maxAskAIImageAttachmentBytes = 5 * 1024 * 1024;
const supportedImageMimeTypes = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
const supportedImageAcceptAttribute = Array.from(supportedImageMimeTypes).join(
  ",",
);

const AskAIForm = forwardRef(
  (
    {
      chatSessionId,
      inputMessage,
      formButtons,
      loadingCreateMessage,
      loadingRetryAskAiMessage,
      imageAttachments,
      autoFocus = false,
      onSubmit,
      onEditorReady,
      onChangeInputValue,
      onChangeImageAttachments,
      onClickPromptSuggestion,
      onClickChatSessionId,
    }: {
      chatSessionId: number | null;
      inputMessage: string;
      imageAttachments: AskAIImageAttachment[];
      autoFocus: boolean;
      loadingCreateMessage: boolean;
      loadingRetryAskAiMessage: boolean;
      onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
      onEditorReady: (editor: Editor) => void;
      onChangeInputValue: (value: string) => void;
      onChangeImageAttachments: Dispatch<
        SetStateAction<AskAIImageAttachment[]>
      >;
      onClickPromptSuggestion: (prompt: string) => void;
      onClickChatSessionId?: (chatSessionId: number | null) => void;
      formButtons?: {
        text: string;
        prompt: string;
        icon: TopicflowIconType;
      }[];
    },
    ref: React.Ref<HTMLFormElement>,
  ) => {
    const { meetingId, assessmentId } = useContext(ChatSessionContext);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [newPrompt, setNewPrompt] = useState("");
    const [isAddCustomPromptModalOpen, setIsAddCustomPromptModalOpen] =
      useState(false);
    const hasSubmittableContent =
      inputMessage.trim().length > 0 ||
      imageAttachments.some((attachment) => !!attachment.imageUrl);
    const isUploadingImages = imageAttachments.some(
      (attachment) => attachment.isUploading,
    );
    const buttonClassName = classNames(
      "rounded-full p-1.5 bg-gray-100 text-gray-500",
      hasSubmittableContent && "bg-blue-link text-white hover:bg-blue-800",
    );
    const currentOrganization = currentOrganizationVar();
    const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

    const { data: skillsData } = useQuery<SkillsQuery>(skillsQuery);
    const skills = useMemo(() => skillsData?.skills ?? [], [skillsData]);

    const extraExtensions = useMemo(() => {
      return [
        SkillCommands.configure({
          items: ({ query }: { query: string }) =>
            skills.filter((skill) =>
              skill.name.startsWith(query.toLowerCase()),
            ),
        }),
      ];
    }, [skills]);

    useEffect(() => {
      if (window.Intercom) {
        window.Intercom("update", { hide_default_launcher: true });
      }
      return function cleanup() {
        if (window.Intercom) {
          window.Intercom("update", { hide_default_launcher: false });
        }
      };
    }, []);

    const customPromptOptions = (
      uiPreferenceCache.askAiCustomPrompts || []
    ).map((prompt) => ({
      type: "button" as const,
      label: prompt,
      labelClassName: "line-clamp-2 max-w-96",
      onClick: () => onClickPromptSuggestion(prompt),
    }));

    const handleSaveNewPrompt = () => {
      if (newPrompt) {
        const askAiCustomPrompts = uiPreferenceCache.askAiCustomPrompts || [];
        saveUiPreference({
          askAiCustomPrompts: [...askAiCustomPrompts, newPrompt.trim()],
        });
        setNewPrompt("");
      }
    };

    const handleDeleteCustomPrompt = (prompt: string) => {
      const askAiCustomPrompts = uiPreferenceCache.askAiCustomPrompts || [];
      saveUiPreference({
        askAiCustomPrompts: askAiCustomPrompts.filter((p) => p !== prompt),
      });
      setNewPrompt("");
    };

    const handleAddCustomPrompt = () => {
      setIsAddCustomPromptModalOpen(true);
    };

    const allPromptOptions = [
      {
        type: "button" as const,
        label: "Manage Prompts",
        onClick: handleAddCustomPrompt,
      },
      {
        type: "divider" as const,
      },
      ...customPromptOptions,
    ];

    const handleCloseAddCustomPromptModal = () => {
      setIsAddCustomPromptModalOpen(false);
      setNewPrompt("");
    };

    const handleRemoveImageAttachment = (attachmentId: string) => {
      onChangeImageAttachments((attachments) => {
        const attachment = attachments.find(({ id }) => id === attachmentId);
        if (attachment) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
        return attachments.filter(({ id }) => id !== attachmentId);
      });
    };

    const handleClickAddImage = () => {
      fileInputRef.current?.click();
    };

    const uploadImageFiles = (files: File[]) => {
      const imageFiles = files.filter((file) =>
        supportedImageMimeTypes.has(file.type),
      );
      if (imageFiles.length !== files.length) {
        errorNotificationVar({
          title: "Unsupported image format.",
          description: "Use PNG, JPEG, WEBP, or GIF.",
        });
      }
      const uploadableImageFiles = imageFiles.filter(
        (file) => file.size <= maxAskAIImageAttachmentBytes,
      );
      if (uploadableImageFiles.length !== imageFiles.length) {
        errorNotificationVar({
          title: "Images must be 5 MB or smaller.",
        });
      }
      const availableSlots = maxAskAIImageAttachments - imageAttachments.length;
      if (availableSlots <= 0) {
        errorNotificationVar({
          title: `You can upload up to ${maxAskAIImageAttachments} images.`,
        });
        return;
      }
      const filesToUpload = uploadableImageFiles.slice(0, availableSlots);
      if (filesToUpload.length < uploadableImageFiles.length) {
        errorNotificationVar({
          title: `You can upload up to ${maxAskAIImageAttachments} images.`,
        });
      }
      const newAttachments = filesToUpload.map((file) => ({
        id: uuidv4(),
        filename: file.name,
        previewUrl: URL.createObjectURL(file),
        isUploading: true,
      }));
      onChangeImageAttachments((attachments) => [
        ...attachments,
        ...newAttachments,
      ]);
      filesToUpload.forEach((file, index) => {
        const attachmentId = newAttachments[index].id;
        asyncUploadImage({ organizationId: currentOrganization.id })(file)
          .then((imageUrl) => {
            if (!imageUrl) {
              throw new Error("The image could not be uploaded.");
            }
            onChangeImageAttachments((attachments) =>
              attachments.map((attachment) =>
                attachment.id === attachmentId
                  ? { ...attachment, imageUrl, isUploading: false }
                  : attachment,
              ),
            );
          })
          .catch(() => {
            handleRemoveImageAttachment(attachmentId);
          });
      });
    };

    const handleChangeImageInput = (e: ChangeEvent<HTMLInputElement>) => {
      uploadImageFiles(Array.from(e.target.files || []));
      e.target.value = "";
    };

    const handlePasteImages = (e: ClipboardEvent<HTMLDivElement>) => {
      const files = Array.from(e.clipboardData.files || []);
      const hasImageFile = files.some((file) => file.type.startsWith("image/"));
      if (!hasImageFile) {
        return;
      }
      e.preventDefault();
      uploadImageFiles(files);
    };

    const response = cache.readQuery<
      GetAskAiChatSessionQueryQuery,
      GetAskAiChatSessionQueryQueryVariables
    >({
      query: getAskAiSessionQuery,
      variables: { chatSessionId: chatSessionId || 0 },
    });
    const cachedChatSession = response?.chatSession;
    const chatSessionTitle = truncate(getChatSessionTitle(cachedChatSession), {
      length: 30,
    });

    return (
      <form
        className={classNames("border rounded-xl bg-white")}
        onSubmit={onSubmit}
        ref={ref}
      >
        <div className="relative" onPaste={handlePasteImages}>
          <div className="absolute right-4 top-4 z-10">
            <Tooltip text="Ask AI is a new feature currently in beta. We are actively improving the experience!">
              <div className="px-2 py-0.5 bg-purple-200 rounded-[14px] text-purple-700 text-xs">
                Beta
              </div>
            </Tooltip>
          </div>
          <TextOnlyWysiwyg
            key={chatSessionId}
            value={inputMessage}
            onChangeValue={onChangeInputValue}
            placeholder={
              meetingId
                ? "Ask anything about this meeting..."
                : assessmentId
                  ? `Message Ask AI`
                  : "Ask anything..."
            }
            className={classNames(
              "js-ask-ai-input",
              "w-full border-none focus:ring-0 focus:outline-none p-4 pr-16",
              "pb-1 min-h-16 max-h-48 overflow-y-auto",
            )}
            editable
            autofocus={autoFocus}
            extraExtensions={extraExtensions}
            onEnterPressed={onSubmit}
            onEditorReady={onEditorReady}
          />
        </div>
        {imageAttachments.length > 0 && (
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
            {imageAttachments.map((attachment) => (
              <div
                key={attachment.id}
                className="group/image relative h-16 w-16 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50"
              >
                <a
                  href={attachment.imageUrl || attachment.previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block h-full w-full"
                >
                  <img
                    src={attachment.previewUrl}
                    alt={attachment.filename}
                    className={classNames(
                      "h-full w-full object-cover",
                      attachment.isUploading && "opacity-60",
                    )}
                  />
                </a>
                {attachment.isUploading && (
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-center text-[10px] font-medium text-white">
                    Uploading
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveImageAttachment(attachment.id)}
                  className="absolute right-1 top-1 hidden rounded-full bg-black/70 p-0.5 text-white hover:bg-black group-hover/image:block focus:block"
                  aria-label={`Remove ${attachment.filename}`}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-between items-center px-4 pb-4">
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={supportedImageAcceptAttribute}
              multiple
              className="hidden"
              onChange={handleChangeImageInput}
            />
            {formButtons &&
              formButtons.map((formButton) => (
                <Button
                  key={formButton.text}
                  onClick={() => {
                    onClickPromptSuggestion(formButton.prompt);
                  }}
                >
                  <TopicflowIcon type="users" className="h-4 w-4" />
                  {formButton.text}
                </Button>
              ))}
            {customPromptOptions.length > 0 ? (
              <Dropdown
                options={allPromptOptions}
                menuClassName="max-h-[400px] overflow-y-auto"
              >
                <Button showDownChevronIcon>Saved Prompts</Button>
              </Dropdown>
            ) : (
              <Button
                icon={getTopicflowIcon("plus")}
                onClick={handleAddCustomPrompt}
                theme={buttonTheme.iconGray}
              />
            )}
            <Button
              icon={PaperClipIcon}
              onClick={handleClickAddImage}
              theme={buttonTheme.iconGray}
              size={ButtonSize.medium}
              tooltip="Upload image"
              aria-label="Upload image"
              disabled={
                imageAttachments.length >= maxAskAIImageAttachments ||
                loadingCreateMessage ||
                loadingRetryAskAiMessage
              }
            />

            {(meetingId || assessmentId) && (
              <ChatHistoryPopover onClickChatSessionId={onClickChatSessionId}>
                {cachedChatSession ? (
                  <AppPopover.Button as={Button}>
                    <TopicflowIcon type="threads" className="h-4 w-4" />
                    {chatSessionTitle}
                  </AppPopover.Button>
                ) : undefined}
              </ChatHistoryPopover>
            )}
          </div>

          <button
            type="submit"
            disabled={
              !hasSubmittableContent ||
              isUploadingImages ||
              loadingCreateMessage ||
              loadingRetryAskAiMessage
            }
            className={buttonClassName}
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
        </div>
        <Modal
          alignedTop
          open={isAddCustomPromptModalOpen}
          onClose={handleCloseAddCustomPromptModal}
        >
          <div className="p-6 flex flex-col gap-6">
            <ModalTitle onClose={handleCloseAddCustomPromptModal}>
              Custom Prompts
            </ModalTitle>
            <div className="flex gap-2">
              <Input
                className="flex-1"
                placeholder="New Prompt..."
                onChange={(e) => setNewPrompt(e.target.value)}
                value={newPrompt}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveNewPrompt();
                  }
                }}
                maxLength={200}
              />
              <Button
                onClick={handleSaveNewPrompt}
                text="Add"
                disabled={newPrompt.trim().length === 0}
              />
            </div>
            <div className="flex flex-col divide-y">
              {(uiPreferenceCache.askAiCustomPrompts || []).map((prompt) => (
                <div key={prompt}>
                  <div
                    className="flex justify-between px-2 py-1 hover:bg-gray-50"
                    role="button"
                    onClick={() => {
                      onClickPromptSuggestion(prompt);
                      handleCloseAddCustomPromptModal();
                    }}
                  >
                    <div className="flex-1 flex items-center">{prompt}</div>
                    <Button
                      icon={getTopicflowIcon("trash")}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteCustomPrompt(prompt);
                      }}
                      theme={buttonTheme.iconGray}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      </form>
    );
  },
);

export default AskAIForm;
